// AI assisted development
/**
 * Feedback API Client Service
 * Feature: 012-user-feedback
 * 
 * API client for user feedback and voting operations.
 */

import { getAccessToken, isAuthenticated } from './auth';
import type {
  FeedbackItem,
  FeedbackReply,
  CreateFeedbackReplyRequest,
  CreateFeedbackRequest,
  UpdateFeedbackRequest,
  UpdateStatusRequest,
  VoteResponse,
  FeedbackListResponse,
} from '../types/feedback';

// Always use the direct API URL (no SWA proxy - we're using MSAL Bearer tokens)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://codereview-dev.azurewebsites.net/api';

/**
 * API Error class for structured error handling.
 */
export class FeedbackApiError extends Error {
  status: number;
  data?: any;
  
  constructor(
    message: string,
    status: number,
    data?: any
  ) {
    super(message);
    this.name = 'FeedbackApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Make authenticated API request with automatic token injection.
 */
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Get access token if authenticated
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // Add any additional headers from options
  if (options.headers) {
    const optHeaders = new Headers(options.headers);
    optHeaders.forEach((value, key) => {
      headers[key] = value;
    });
  }
  
  if (isAuthenticated()) {
    try {
      const token = await getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.error('Failed to get access token:', error);
      // Continue without token (API may have auth disabled)
    }
  }
  
  // Make request
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'same-origin', // Include cookies for Static Web Apps auth
  });
  
  // Handle errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new FeedbackApiError(
      errorData.message || errorData.error || `HTTP ${response.status}`,
      response.status,
      errorData
    );
  }
  
  // Parse response
  return await response.json();
}

/**
 * Feedback API client with typed methods.
 */
export const feedbackApi = {
  /**
   * GET /api/feedback - Get all feedback items
   */
  getAll: async (status?: string): Promise<FeedbackListResponse> => {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await fetchWithAuth<{ items: FeedbackItem[]; total: number }>(`/feedback${params}`);
    return {
      items: response.items,
      total: response.total,
    };
  },

  /**
   * GET /api/feedback/{id} - Get single feedback item
   */
  getById: async (id: number): Promise<FeedbackItem> => {
    return fetchWithAuth<FeedbackItem>(`/feedback/${id}`);
  },

  /**
   * POST /api/feedback - Create new feedback
   */
  create: async (request: CreateFeedbackRequest): Promise<FeedbackItem> => {
    return fetchWithAuth<FeedbackItem>('/feedback', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * PUT /api/feedback/{id} - Update feedback (author only)
   */
  update: async (id: number, request: UpdateFeedbackRequest): Promise<FeedbackItem> => {
    return fetchWithAuth<FeedbackItem>(`/feedback/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  /**
   * PATCH /api/feedback/{id}/status - Update status (any user)
   */
  updateStatus: async (id: number, request: UpdateStatusRequest): Promise<FeedbackItem> => {
    return fetchWithAuth<FeedbackItem>(`/feedback/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
  },

  /**
   * POST /api/feedback/{id}/vote - Vote on feedback
   */
  vote: async (id: number): Promise<VoteResponse> => {
    return fetchWithAuth<VoteResponse>(`/feedback/${id}/vote`, {
      method: 'POST',
    });
  },

  /**
   * POST /api/feedback/{id}/replies - Reply to feedback
   */
  createReply: async (id: number, request: CreateFeedbackReplyRequest): Promise<FeedbackReply> => {
    return fetchWithAuth<FeedbackReply>(`/feedback/${id}/replies`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },
};

